import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';
import { WebPostDataItem } from './types';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-form-web-post-data',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule
],
  templateUrl: './form-web-post-data.component.html',
  styleUrl: './form-web-post-data.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormWebPostDataComponent),
      multi: true,
    },
  ],
})
export class FormWebPostDataComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input() controlName: string = '';
  @Input() languages: string[] = ['en'];
  @Input() formControlPath: string = '';

  form: FormGroup;
  private destroy$ = new Subject<void>();
  private onChange = (value: WebPostDataItem[]) => {};
  private onTouched = () => {};
  isLoading = true;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      items: this.fb.array([]),
    });

    this.createInitialEnglishItem();
  }

  ngOnInit() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onChange(value.items);
      this.onTouched();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['languages']) {
      if (!changes['languages'].firstChange) {
        this.updateFormControls();
      }
    }
  }

  private createInitialEnglishItem() {
    if (this.itemsArray.length === 0) {
      const englishItem = this.createEnglishOnlyItem();
      this.itemsArray.push(englishItem);
    }
  }

  private updateFormControls() {
    this.itemsArray.controls.forEach((control, index) => {
      const titleControl = control.get('title') as FormGroup;
      const textControl = control.get('text') as FormGroup;

      if (titleControl && textControl) {
        this.languages.forEach((lang) => {
          if (!titleControl.contains(lang)) {
            titleControl.addControl(lang, this.fb.control(''));
          }
          if (!textControl.contains(lang)) {
            textControl.addControl(lang, this.fb.control(''));
          }
        });

        const existingTitleLangs = Object.keys(titleControl.controls);
        const existingTextLangs = Object.keys(textControl.controls);

        existingTitleLangs.forEach((lang) => {
          if (!this.languages.includes(lang)) {
            titleControl.removeControl(lang);
          }
        });

        existingTextLangs.forEach((lang) => {
          if (!this.languages.includes(lang)) {
            textControl.removeControl(lang);
          }
        });
      }
    });
  }

  private createEnglishOnlyItem(): FormGroup {
    const titleGroup = this.fb.group({
      en: [''],
    });
    const textGroup = this.fb.group({
      en: [''],
    });

    return this.fb.group({
      date_time: [''],
      title: titleGroup,
      text: textGroup,
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemsArray(): FormArray {
    return this.form.get('items') as FormArray;
  }

  addItem() {
    const newItem = this.createEmptyItem();
    this.itemsArray.push(newItem);
  }

  removeItem(index: number) {
    this.itemsArray.removeAt(index);
  }

  private createEmptyItem(): FormGroup {
    const titleGroup = this.fb.group({});
    const textGroup = this.fb.group({});

    if (this.languages && this.languages.length > 0) {
      this.languages.forEach((lang) => {
        titleGroup.addControl(lang, this.fb.control(''));
        textGroup.addControl(lang, this.fb.control(''));
      });
    } else {
      titleGroup.addControl('en', this.fb.control(''));
      textGroup.addControl('en', this.fb.control(''));
    }

    return this.fb.group({
      date_time: [''],
      title: titleGroup,
      text: textGroup,
    });
  }

  writeValue(value: WebPostDataItem[]): void {
    if (value && Array.isArray(value)) {
      this.itemsArray.clear();
      value.forEach((item) => {
        const itemGroup = this.createEmptyItem();
        itemGroup.patchValue(item);
        this.itemsArray.push(itemGroup);
      });
    } else if (this.itemsArray.length === 0) {
      this.createInitialEnglishItem();
    }
  }

  registerOnChange(fn: (value: WebPostDataItem[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  addLanguage(lang: string) {
    if (!this.languages.includes(lang)) {
      this.languages.push(lang);
      this.updateFormControls();
    }
  }

  removeLanguage(lang: string) {
    const index = this.languages.indexOf(lang);
    if (index > -1 && this.languages.length > 1) {
      this.languages.splice(index, 1);
      this.updateFormControls();
    }
  }
}
